import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionFab } from '../../../components/ActionButton'
import { FilterBar, FilterSearch } from '../../../components/FilterBar'
import { LoadingBlock } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { StatusBadge } from '../../../components/StatusBadge'
import { useHasRole, useAppToast } from '../../../helpers'
import { StudyCreditRequestService } from '../../../services/StudyCreditRequest/StudyCreditRequestService'
import { IStudyCreditRequestRowRaw, StudyCreditStatus } from '../../../services/StudyCreditRequest/types'
import { CommercialDialog } from './CommercialDialog'
import { STUDY_CREDIT_STATUS, STUDY_CREDIT_STATUSES } from './consts'
import { MissingDocsDialog } from './MissingDocsDialog'
import { StudyCreditKanbanCard, StudyCreditKanbanCardContent } from './StudyCreditKanbanCard'
import { StudyCreditViewModal } from './StudyCreditViewModal'

// Each column loads this many cards at a time; "Carregar mais" fetches the next page.
const PAGE_SIZE = 8

interface IColumnState {
  items: IStudyCreditRequestRowRaw[]
  total: number
  loadingMore: boolean
}

type BoardState = Record<StudyCreditStatus, IColumnState>

const emptyBoard = (): BoardState =>
  STUDY_CREDIT_STATUSES.reduce((acc, status) => {
    acc[status] = { items: [], total: 0, loadingMore: false }
    return acc
  }, {} as BoardState)

interface IKanbanColumnProps {
  status: StudyCreditStatus
  column: IColumnState
  dragDisabled: boolean
  onOpenCard: (id: number) => void
  onLoadMore: (status: StudyCreditStatus) => void
}

function KanbanColumn({ status, column, dragDisabled, onOpenCard, onLoadMore }: IKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status })
  const remaining = column.total - column.items.length

  return (
    <Flex
      ref={setNodeRef}
      direction="column"
      flex="1"
      minW="300px"
      bg={isOver ? 'gray.100' : 'gray.50'}
      borderWidth="1px"
      borderColor={isOver ? 'magenta.500' : 'gray.200'}
      borderRadius="lg"
      p={3}
    >
      <Flex align="center" justify="space-between" px={1} py={1} mb={2}>
        <Text fontWeight="bold" fontSize="sm">
          {STUDY_CREDIT_STATUS[status].label}
        </Text>
        <StatusBadge status={status} statuses={STUDY_CREDIT_STATUS} label={String(column.total)} />
      </Flex>
      <Flex
        direction="column"
        gap={2}
        overflowY="auto"
        maxH="calc(100vh - 230px)"
        minH="60px"
        sx={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0,0,0,0.18) transparent',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { bg: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bg: 'rgba(0,0,0,0.18)', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb:hover': { bg: 'rgba(0,0,0,0.3)' }
        }}
      >
        {column.items.map((request) => (
          <StudyCreditKanbanCard key={request.id} request={request} dragDisabled={dragDisabled} onOpen={onOpenCard} />
        ))}
        {remaining > 0 && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="magenta"
            onClick={() => onLoadMore(status)}
            isLoading={column.loadingMore}
          >
            Carregar mais ({remaining})
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export default function StudyCreditKanbanPage() {
  const service = new StudyCreditRequestService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canEdit = hasRole('edit_study_credit_requests')
  const canDelete = hasRole('delete_study_credit_requests')

  const [searchParams, setSearchParams] = useSearchParams()
  const [board, setBoard] = useState<BoardState>(() => emptyBoard())
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  // O `FilterSearch` já entrega o texto debounced, então só existe um estado de busca.
  // Guarda o texto cru: trimar aqui voltaria para o campo controlado e comeria o espaço
  // recém-digitado. Quem consome é que apara — veja `searchTerm`.
  const [search, setSearch] = useState('')
  const searchTerm = search.trim()
  const [openCardId, setOpenCardId] = useState<number | null>(null)
  const [activeCard, setActiveCard] = useState<IStudyCreditRequestRowRaw | null>(null)
  const [pendingMissingDocs, setPendingMissingDocs] = useState<IStudyCreditRequestRowRaw | null>(null)
  const [pendingCommercial, setPendingCommercial] = useState<IStudyCreditRequestRowRaw | null>(null)
  // Suppresses the click that browsers fire right after a drop, which would open the card modal.
  const lastDragEndRef = useRef(0)
  const isInitialLoadRef = useRef(true)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  )

  // Fetches the first page of every column; used on mount and whenever the search changes.
  const loadBoard = async (searchTerm: string, initial: boolean) => {
    if (initial) setLoading(true)
    else setRefreshing(true)
    try {
      const pages = await Promise.all(
        STUDY_CREDIT_STATUSES.map((status) => service.list({ status, search: searchTerm, offset: 0, limit: PAGE_SIZE }))
      )
      const next = emptyBoard()
      STUDY_CREDIT_STATUSES.forEach((status, i) => {
        next[status] = { items: pages[i].data, total: pages[i].total, loadingMore: false }
      })
      setBoard(next)
    } catch {
      toast.error('Erro ao carregar as solicitações')
    } finally {
      if (initial) setLoading(false)
      else setRefreshing(false)
    }
  }

  useEffect(() => {
    loadBoard(searchTerm, isInitialLoadRef.current)
    isInitialLoadRef.current = false
  }, [searchTerm])

  // Deep link from the automation e-mails: /aproveitamento-de-estudos?card={id}
  useEffect(() => {
    const cardParam = Number(searchParams.get('card'))
    if (cardParam) setOpenCardId(cardParam)
  }, [searchParams])

  const loadMore = async (status: StudyCreditStatus) => {
    setBoard((prev) => ({ ...prev, [status]: { ...prev[status], loadingMore: true } }))
    try {
      const page = await service.list({
        status,
        search: searchTerm,
        offset: board[status].items.length,
        limit: PAGE_SIZE
      })
      setBoard((prev) => {
        // A search/refresh may have reset this column while the request was in flight; ignore the stale page.
        if (!prev[status].loadingMore) return prev
        const seen = new Set(prev[status].items.map((r) => r.id))
        const merged = [...prev[status].items, ...page.data.filter((r) => !seen.has(r.id))]
        return { ...prev, [status]: { items: merged, total: page.total, loadingMore: false } }
      })
    } catch {
      setBoard((prev) =>
        prev[status].loadingMore ? { ...prev, [status]: { ...prev[status], loadingMore: false } } : prev
      )
      toast.error('Erro ao carregar mais cards')
    }
  }

  const handleOpenCard = (id: number) => {
    if (Date.now() - lastDragEndRef.current < 250) return
    setOpenCardId(id)
  }

  const handleCloseCard = () => {
    setOpenCardId(null)
    if (searchParams.has('card')) setSearchParams({}, { replace: true })
  }

  // Re-syncs a column's first page with the server after an optimistic move. Offset
  // pagination doesn't survive optimistic inserts/removes (items.length no longer
  // matches the server ordering), so "Carregar mais" could skip or duplicate a card;
  // reloading the affected columns realigns them. Best-effort: a failure just leaves
  // the optimistic state until the next full refresh.
  const resyncColumn = async (status: StudyCreditStatus, depth: number) => {
    const limit = Math.min(100, Math.max(PAGE_SIZE, depth))
    try {
      const page = await service.list({ status, search: searchTerm, offset: 0, limit })
      setBoard((prev) => ({
        ...prev,
        [status]: { ...prev[status], items: page.data, total: page.total, loadingMore: false }
      }))
    } catch {
      // Ignore: the board reconciles on the next search/refresh.
    }
  }

  // Moves a card across columns, keeping each column's count in sync so pagination stays aligned.
  const moveCard = async (request: IStudyCreditRequestRowRaw, status: StudyCreditStatus, note?: string) => {
    const from = request.status
    // Depths captured before the optimistic update so the resync reloads the same number of cards.
    const fromDepth = board[from].items.length - 1
    const toDepth = board[status].items.length + 1
    setBoard((prev) => ({
      ...prev,
      [from]: {
        ...prev[from],
        items: prev[from].items.filter((r) => r.id !== request.id),
        total: Math.max(0, prev[from].total - 1)
      },
      [status]: {
        ...prev[status],
        items: [{ ...request, status }, ...prev[status].items.filter((r) => r.id !== request.id)],
        total: prev[status].total + 1
      }
    }))
    try {
      const { notified } = await service.updateStatus(request.id, status, note)
      if (status === 'missing_docs' || status === 'commercial') {
        if (notified) {
          toast.success('Card movido e consultor notificado por e-mail')
        } else {
          toast.warning('Card movido', 'O consultor não possui e-mail cadastrado; nenhuma notificação foi enviada.')
        }
      }
      // Realign both affected columns with the server ordering (fire-and-forget).
      void Promise.all([resyncColumn(from, fromDepth), resyncColumn(status, toDepth)])
    } catch {
      // Roll the optimistic move back without a round-trip.
      setBoard((prev) => ({
        ...prev,
        [status]: {
          ...prev[status],
          items: prev[status].items.filter((r) => r.id !== request.id),
          total: Math.max(0, prev[status].total - 1)
        },
        [from]: {
          ...prev[from],
          items: [{ ...request, status: from }, ...prev[from].items.filter((r) => r.id !== request.id)],
          total: prev[from].total + 1
        }
      }))
      toast.error('Erro ao mover o card')
      throw new Error('move failed')
    }
  }

  const allItems = STUDY_CREDIT_STATUSES.flatMap((status) => board[status].items)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveCard(allItems.find((r) => String(r.id) === event.active.id) ?? null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    lastDragEndRef.current = Date.now()
    setActiveCard(null)

    const { active, over } = event
    if (!over) return

    const status = over.id as StudyCreditStatus
    const request = allItems.find((r) => String(r.id) === active.id)
    if (!request || request.status === status) return

    if (status === 'missing_docs') {
      // The move is only applied after the observations are confirmed in the dialog.
      setPendingMissingDocs(request)
      return
    }

    if (status === 'commercial') {
      // The move is only applied after the consultant notification is confirmed.
      setPendingCommercial(request)
      return
    }

    moveCard(request, status).catch(() => undefined)
  }

  return (
    // O `w="100%"` é o que dá largura definida ao contêiner de rolagem horizontal das colunas
    // do kanban; sem ele as colunas esticam a página em vez de rolar dentro dela.
    <Box mt={4} w="100%">
      <PageTitle
        title="Aproveitamento de Estudos"
        description="Solicitações de aproveitamento e seu estágio no fluxo"
        mb={4}
      />

      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch value={search} onChange={setSearch} placeholder="Buscar por nome ou CPF..." maxW="480px" />
        {refreshing && <Spinner size="sm" color="magenta.500" flexShrink={0} alignSelf="center" mb="1" />}
      </FilterBar>

      {loading ? (
        <LoadingBlock size="page" />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveCard(null)}
        >
          <Flex
            gap={4}
            align="flex-start"
            overflowX="auto"
            pb={4}
            sx={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,0,0,0.18) transparent',
              '&::-webkit-scrollbar': { height: '8px' },
              '&::-webkit-scrollbar-track': { bg: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bg: 'rgba(0,0,0,0.18)', borderRadius: '10px' },
              '&::-webkit-scrollbar-thumb:hover': { bg: 'rgba(0,0,0,0.3)' }
            }}
          >
            {STUDY_CREDIT_STATUSES.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                column={board[status]}
                dragDisabled={!canEdit}
                onOpenCard={handleOpenCard}
                onLoadMore={loadMore}
              />
            ))}
          </Flex>
          {/* Portaled copy that follows the pointer, escaping the columns' overflow clipping. */}
          <DragOverlay>
            {activeCard ? <StudyCreditKanbanCardContent request={activeCard} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {openCardId !== null && (
        <StudyCreditViewModal
          requestId={openCardId}
          isOpen={openCardId !== null}
          canEdit={canEdit}
          canDelete={canDelete}
          onClose={handleCloseCard}
          onDeleted={() => {
            handleCloseCard()
            loadBoard(searchTerm, false)
          }}
        />
      )}

      <MissingDocsDialog
        isOpen={pendingMissingDocs !== null}
        consultantName={pendingMissingDocs?.consultant?.name}
        onClose={() => setPendingMissingDocs(null)}
        onConfirm={async (note) => {
          if (pendingMissingDocs) await moveCard(pendingMissingDocs, 'missing_docs', note)
        }}
      />

      <CommercialDialog
        isOpen={pendingCommercial !== null}
        consultantName={pendingCommercial?.consultant?.name}
        onClose={() => setPendingCommercial(null)}
        onConfirm={async () => {
          if (pendingCommercial) await moveCard(pendingCommercial, 'commercial')
        }}
      />

      <ActionFab label="Nova solicitação" to="/aproveitamento-de-estudos/new" openInNewTab />
    </Box>
  )
}
